import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { twMerge } from "tailwind-merge";
import { useBreakpoint } from "@shared/hooks/useBreakpoint";
import { Type } from "@shared/ui/Type";
import triangle from "@shared/assets/images/triangle.png";

type Props = {
  title: string;
  subheader?: string;
  children: string;
  defaultOpen?: boolean;
  isAccordion?: boolean;
  className?: string;
};

export const TextItem = ({
  title,
  subheader,
  children,
  defaultOpen = false,
  isAccordion = false,
  className,
}: Props) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const isMdScreen = useBreakpoint("md");

  useEffect(() => {
    if (isMdScreen && isAccordion) {
      setIsOpen(true);
    }
  }, [isAccordion, isMdScreen]);

  const toggleAccordion = () => {
    if (isAccordion) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={twMerge("flex flex-col gap-2", className)}>
      <div
        className={`flex flex-row gap-2 ${isAccordion ? "cursor-pointer" : ""}`}
        onClick={toggleAccordion}
      >
        {isAccordion && (
          <img
            src={triangle}
            className={`relative top-1.5 w-[11px] h-[12px] transition-transform duration-200 md:w-[17px] md:h-[18px] ${
              isOpen ? "rotate-0" : "-rotate-90"
            }`}
            alt="triangle"
          />
        )}
        <Type variant={"h4"} as="span">
          {title}
        </Type>
      </div>
      {(!isAccordion || isOpen) && (
        <div className="transition-all duration-300">
          <div className="flex flex-col gap-2">
            {subheader && (
              <Type variant="body2" as="span">
                {subheader}
              </Type>
            )}
            <Type variant="body2">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      className="underline text-brand-800 hover:text-brand-700"
                      data-testid="text-item-link"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {children}
              </ReactMarkdown>
            </Type>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextItem;
